<script setup>
import { ref, onBeforeMount, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import * as z from 'zod'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import parsePhoneNumber from 'libphonenumber-js'
import * as firebaseui from 'firebaseui'
import firebase from 'firebase/compat/app'
import { firebase as routerFirebase } from '@/router'
import {onAuthStateChanged} from 'firebase/auth'

import { Input } from '@/components/ui/input'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'

import googlePlayBadge from '@/assets/img/google-play-badge.png'
import appStoreBadge from '@/assets/img/app-store-badge.svg'

const breakpoints = useBreakpoints(breakpointsTailwind)

const router = useRouter()
const route = useRoute()
const inviteCode = route.params.inviteCode

const store = useStore()
const isLoggedIn = computed(() => store.getters.getIsLoggedIn)

const invite = ref(null)
const isShowForm = ref(false)
const isFirebase = ref(false)
const firebaseUser = ref(null)
const isMobile = breakpoints.sm || breakpoints.md
const joinFromWeb = ref(isMobile)
const initialValues = ref(null)

const openAppUrl = ref('')

const openApp = () => {
  const isAndroid = /Android/i.test(navigator.userAgent) || navigator.platform.toLowerCase().includes('android');

  if (isAndroid) {
    const intentUrl = window.location.href.replace('https', 'intent')
    window.location.href = `${intentUrl}#Intent;scheme=https;package=com.earthpbc.explorer;end`
  } else {
    let protocol = window.location.protocol
    let url = `earthpbc://${window.location.host}/invite/${inviteCode}`
    window.location.href = url
  }
}

const phoneFieldSchema = z
  .string()
  .optional()
  .nullable()
  .transform((value, ctx) => {
    if (!value) return null

    const phoneNumber = parsePhoneNumber(value, { defaultCountry: 'US' })
    if (!phoneNumber?.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid or incomplete phone number',
      })
      return z.NEVER
    }

    return phoneNumber.number
  })

const formSchema = ref(z.object({
  name: z.string().min(1, 'Name is required').max(255),
  email: z.string().email('Invalid email address').optional().nullable(),
  phone: phoneFieldSchema,
  contacts: z.array(phoneFieldSchema).default([]),
}))

const joinProject = async () => {
  isFirebase.value = true

  const emailProviders = [
    {
        provider: firebase.auth.GoogleAuthProvider.PROVIDER_ID,
        customParameters: { prompt: 'select_account'}
    },
    {
      provider: firebase.auth.EmailAuthProvider.PROVIDER_ID,
      signInMethod: firebase.auth.EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD
    },
  ]

  const phoneProviders = [
    {
      provider: firebase.auth.PhoneAuthProvider.PROVIDER_ID
    }
  ]

  const providers = invite.value.is_email ? emailProviders : invite.value.is_phone ? phoneProviders : emailProviders.concat(phoneProviders)

  let ui = firebaseui.auth.AuthUI.getInstance() || new firebaseui.auth.AuthUI(firebase.auth())
  ui.start('#firebaseui-auth-container', {
    signInFlow: 'popup',
    credentialHelper: firebaseui.auth.CredentialHelper.GOOGLE_YOLO,
    signInOptions: providers,
    callbacks: {
      signInSuccessWithAuthResult: function(authResult, redirectUrl) {
        // cleanup url params
        const url = new URL(window.location.href)
        url.searchParams.delete('mode')
        url.searchParams.delete('oobCode')
        window.history.replaceState({}, document.title, url.pathname)
      },
      signInFailure: async (error) => {
        console.warn("[login-window] ERROR" + error);
      },
    }
  })
}

const back = () => {
  firebase.auth().signOut()
  isFirebase.value = false
  isShowForm.value = false
  firebaseUser.value = null
}

const close = () => {
  if (!isLoggedIn.value) {
    router.push({ name: 'AuthLogin' })
  } else {
    router.push({ name: 'Dashboard' })
  }
}

const getInvite = async (firebase_token) => {
  try {
    const response = await API({
      method: 'GET',
      url: !!firebase_token ? `${HOST}/invite/${inviteCode}?firebase_token=${firebase_token}` : `${HOST}/invite/${inviteCode}`
    })
    invite.value = response.data ? response.data : null
  } catch (error) {
    console.error(error)
  }
}

onBeforeMount(async () => {
  await getInvite()

  onAuthStateChanged(routerFirebase.auth, async (user) => {
    if (user) {
      await getInvite(user.accessToken)

      firebaseUser.value = user
      isFirebase.value = false
      isShowForm.value = !!invite.value

      if (invite.value) {
        initialValues.value = {
          name: invite.value.fullname || '',
          email: invite.value.email || '',
          phone: invite.value.phone || ''
        }
      }
    }
  })
})

async function onSubmit(values) {
  try {
    const idToken = await firebaseUser.value.getIdToken(true)

    const result = await store.dispatch('user/handleAuthentication', {
      firebaseToken: idToken,
      inviteCode: inviteCode,
      inviteDetails: {
        display_name: values.name,
        email: values.email,
        phone: values.phone
      }
    })

    if (result.success) {
      router.push({ name: 'Dashboard' })
    } else {
      console.error('Authentication failed:', result.reason)

      toast({
        title: 'Sign in failed',
        description: result.reason === 'error'
          ? 'Failed to process invitation. Please try again.'
          : `Authentication failed: ${result.reason}`
      })
    }
  } catch (error) {
    console.error('Error submitting invite form:', error)

    toast({
      title: 'Sign in failed',
      description: error.response?.data?.detail || 'Failed to process invitation. Please try again.'
    })
  }
}
</script>

<template>
  <div class="">
    <div v-if="!joinFromWeb" class="flex flex-col items-center justify-center p-4 md:p-6 lg:p-8">
      <h1 class="text-xl font-semibold">Get the Earth App</h1>
      <p class="text-gray-500 text-center max-w-lg">
        If you don't have the app, you can download it here:
      </p>

      <div class="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 my-5">
        <Button
          as="a"
          :href="'https://play.google.com/store/apps/details?id=com.earthpbc.explorer'"
          target="_blank"
          size="lg"
          class="p-0 bg-transparent hover:bg-gray-100 transition inline-flex items-center justify-center"
        >
          <img
            :src="googlePlayBadge"
            alt="Get it on Google Play"
            class="h-16"
          />
        </Button>

        <Button
          as="a"
          :href="'https://apps.apple.com/us/app/earth-pbc/id1590563602'"
          target="_blank"
          size="lg"
          class="p-0 bg-transparent hover:bg-gray-100 transition inline-flex items-center justify-center"
        >
          <img
            :src="appStoreBadge"
            alt="Download on the App Store"
            class="h-12"
          />
        </Button>
      </div>

      <p class="text-gray-500 text-center max-w-lg my-4">
        Or open the app if you already have it installed.
      </p>
      {{ openAppUrl }}
      <Button
        size="lg"
        @click="openApp"
      >
        Open in Earth App
      </Button>

      <div class="mt-6">
        <Button
          size="lg"
          variant="link"
          @click="joinFromWeb = true"
        >
          Join invite on the web
        </Button>
      </div>
    </div>

    <div v-else class="p-1">
      <div class="mb-4">
        <img src="@/assets/img/earth-art.png" alt="Earth" class="md:max-w-full md:w-[400px]">
      </div>

      <div>

        <div v-show="isFirebase">
          <div id="firebaseui-auth-container"></div>
        </div>

        <div v-if="!isFirebase && !isShowForm" class="md:p-1">
          <div v-if="invite" class="mb-4 text-left">
            <div class="mt-4">
              <h3 class="mb-2">Hi, <b>{{ invite.fullname }}</b></h3>
              <p>
                {{ invite.inviter_name }} invited you to join <br />
                the <b>{{ invite.project_name }}</b>.
              </p>
            </div>
            <div class="space-x-2 mt-6">
              <Button size="lg" class="font-semibold" @click="joinProject">
                Join now
              </Button>
              <Button size="lg" variant="outline" @click="close">
                Close
              </Button>
            </div>
          </div>

          <div v-else class="text-center">
            <p class="mt-4 mb-3">Invite expired or not exists</p>
            <Button @click="close">
              Close
            </Button>
          </div>
        </div>

        <div v-if="isShowForm">
          <div class="mb-3">
            <h2 class="font-bold">Complete your profile</h2>
            <p class="text-gray-500 text-sm">Please provide your details to complete your profile</p>
          </div>

          <Form
            v-if="initialValues"
            :form-schema="formSchema"
            :initial-values="initialValues"
            @submit="onSubmit"
            class="space-y-4"
          >
            <FormField name="name" v-slot="{ componentField }">
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField name="email" v-slot="{ componentField }">
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField name="phone" v-slot="{ componentField }">
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <div class="flex justify-end space-x-2">
              <Button type="button" variant="ghost" @click="back">Back</Button>
              <Button type="submit">Save</Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>
